<template>
  <div class="table-owner-cell">
    <BittsSelect
      :model-value="selectedUserId"
      :options="ownerOptions"
      :searchable="true"
      placeholder="Assign"
      size="small"
      dropdown-class-name="table-owner-cell__dropdown"
      :dropdown-match-select-width="false"
      class="table-owner-cell__select"
      aria-label="Owner"
      @change="handleOwnerChange"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import { useForwardCellFocus } from '@/composables/useForwardCellFocus';
import { useTeamStore } from '@/stores';
import { buildOwnerOptions } from '@/utils/ownerOptions';

import type { TableCellOwner, TableCellOwnerOption } from './types';

const { params } = defineProps<{
  params: ICellRendererParams;
}>();

useForwardCellFocus(params.eGridCell);

const teamStore = useTeamStore();

const owner = computed(() => (params.value ?? null) as TableCellOwner);
const selectedUserId = computed(() => owner.value?.user_id ?? null);

/**
 * The option list is identical for every row, so a table with many rows should
 * build it once and hand it over through the grid context. Falling back to the
 * store keeps tables that have not done so working.
 */
const teamOptions = computed<TableCellOwnerOption[]>(
  () =>
    params.context?.teamOwnerOptions?.() ??
    buildOwnerOptions(teamStore.coreUsers),
);

const ownerOptions = computed(() => {
  const current = owner.value;
  // The owner may not be a core user (e.g. a deactivated seat), so append them
  // to keep the select showing a name instead of a raw id.
  if (current && !teamOptions.value.some((o) => o.value === current.user_id)) {
    return [
      ...teamOptions.value,
      { value: current.user_id, label: current.name },
    ];
  }
  return teamOptions.value;
});

function handleOwnerChange(userId: number | null) {
  params.context?.updateRowOwner?.(params.data?.id, userId);
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-owner-cell {
  @apply flex items-center h-full w-full min-w-0;
}
.table-owner-cell__select {
  @apply min-w-[100px] max-w-full;

  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply h-24 min-h-[24px] rounded-6 shadow-none;
  }

  &.bitts-select .ant-select-selection-item {
    @apply text-sm leading-[22px];
  }

  &.bitts-select .ant-select-selection-placeholder {
    @apply text-sm leading-[22px];
  }

  &.bitts-select .ant-select-arrow {
    @apply right-6;
  }
}
.table-owner-cell__dropdown {
  @apply min-w-[200px];
}
</style>
